import logoHoros from '../assets/logos/logo-horos.png';
import logoIndexa from '../assets/logos/logo-indexa.png';
import logoMagallanes from '../assets/logos/logo-magallanes.png';
import logoCaser from '../assets/logos/logo-caser.png';

export enum ArcaPlanId {
	HOROS = 'horos',
	INDEXA = 'indexa',
	MAGALLANES = 'magallanes',
	HOROS_AUT = 'horos-aut',
	INDEXA_AUT_CICLO = 'indexa-aut-ciclo',
	INDEXA_AUT_ACCIONES = 'indexa-aut-acciones',
	CASER_AUT = 'caser-aut'
}

export interface ArcaPlan {
	id: ArcaPlanId;
	dgsfpCode: string;
	catalogSlug: string;
	name: string;
	manager: string;
	style: string;
	logo: string;
	commissionMgmt: number;
	commissionDeposit: number;
	isFreelanceOnly?: boolean;
	/**
	 * Perfil de riesgo oficial del plan (escala 1-7 del KIID/DFP).
	 * TODO: niveles orientativos, verificar con los KIID oficiales antes de publicar.
	 */
	riskProfile?: {
		level: number;
		description: string;
	};
	/** URL del PDF del KIID, si está disponible. `undefined` hasta tenerla en BD. */
	kiidUrl?: string;
}

/**
 * Plans only available for direct traffic (no partner).
 * Vacío desde que negocio decidió permitir Indexa también a usuarios de partner
 * (alineado con el proyecto de onboarding). Se mantiene la maquinaria por si en
 * el futuro hubiera que volver a restringir algún plan al tráfico directo.
 */
const DIRECT_ONLY_PLANS = new Set<ArcaPlanId>([]);

export const ARCA_PLANS: ArcaPlan[] = [
	{
		id: ArcaPlanId.HOROS,
		dgsfpCode: 'N5263',
		catalogSlug: 'horos-internacional-pp',
		name: 'Horos Value Internacional PP',
		manager: 'Horos AM',
		style: 'Value Investing Internacional',
		logo: logoHoros,
		commissionMgmt: 1.5,
		commissionDeposit: 0.15,
		riskProfile: { level: 5, description: '100% renta variable global, gestión valor' }
	},
	{
		id: ArcaPlanId.INDEXA,
		dgsfpCode: 'N5138',
		catalogSlug: 'indexa-mas-rentabilidad-acciones-ppi',
		name: 'Indexa Más Rentabilidad Acciones PP',
		manager: 'Indexa Capital',
		style: 'Gestión Indexada',
		logo: logoIndexa,
		commissionMgmt: 0.52,
		commissionDeposit: 0.08,
		riskProfile: { level: 5, description: '100% renta variable global indexada' }
	},
	{
		id: ArcaPlanId.MAGALLANES,
		dgsfpCode: 'N3666',
		catalogSlug: 'caser-magallanes-pp',
		name: 'CASER MAGALLANES, PP',
		manager: 'Magallanes',
		style: 'Value Investing Europeo',
		logo: logoMagallanes,
		commissionMgmt: 1.5,
		commissionDeposit: 0.15,
		riskProfile: { level: 6, description: '100% renta variable europea, gestión valor' }
	},
	// Planes de autónomos (PPES). Su rentabilidad, como la de todos, la decide comparsa.
	{
		id: ArcaPlanId.HOROS_AUT,
		dgsfpCode: 'N5546',
		catalogSlug: 'horos-internacional-autonomos-ppes',
		name: 'Horos Internacional Autónomos',
		manager: 'Horos AM',
		style: 'Value Investing Internacional',
		logo: logoHoros,
		commissionMgmt: 1.5,
		commissionDeposit: 0.15,
		isFreelanceOnly: true,
		riskProfile: { level: 6, description: '100% renta variable global, gestión valor' },
		// Documento oficial (DFP) alojado en el bucket de planes.
		kiidUrl:
			'https://storage.googleapis.com/arca-plans-documents/DFP/DFP_horos-internacional-autonomos.pdf'
	},
	{
		id: ArcaPlanId.INDEXA_AUT_CICLO,
		dgsfpCode: 'N5489',
		catalogSlug: 'indexa-empleo-autonomos-ppes',
		name: 'Indexa Autónomos Ciclo de Vida',
		manager: 'Indexa Capital',
		style: 'Gestión Indexada (ciclo de vida)',
		logo: logoIndexa,
		commissionMgmt: 0.52,
		commissionDeposit: 0.08,
		isFreelanceOnly: true,
		riskProfile: {
			level: 4,
			description: 'mezcla de renta variable y fija según ciclo de vida'
		},
		// Documento oficial (DFP) alojado en el bucket de planes.
		kiidUrl:
			'https://storage.googleapis.com/arca-plans-documents/DFP/DFP_indexa-autonomos-ciclo-vida.pdf'
	},
	{
		id: ArcaPlanId.INDEXA_AUT_ACCIONES,
		dgsfpCode: 'N5568',
		catalogSlug: 'indexa-empleo-auto-100-acciones-ppes',
		name: 'Indexa Autónomos 100% Acciones',
		manager: 'Indexa Capital',
		style: 'Gestión Indexada',
		logo: logoIndexa,
		commissionMgmt: 0.52,
		commissionDeposit: 0.08,
		isFreelanceOnly: true,
		riskProfile: { level: 6, description: '100% renta variable global indexada' },
		// Documento oficial (DFP) alojado en el bucket de planes.
		kiidUrl:
			'https://storage.googleapis.com/arca-plans-documents/DFP/DFP_indexa-autonomos-acciones.pdf'
	},
	{
		id: ArcaPlanId.CASER_AUT,
		dgsfpCode: 'N5556',
		catalogSlug: 'caser-evolucion-autonomos-ppes',
		name: 'Caser Evolución Autónomos',
		manager: 'Caser',
		style: 'Renta Fija',
		logo: logoCaser,
		commissionMgmt: 0.85,
		commissionDeposit: 0.15,
		isFreelanceOnly: true,
		riskProfile: { level: 2, description: 'predominio de renta fija' }
	}
];

/** Values in transfer.origin that indicate direct traffic (no partner). */
const DIRECT_ORIGINS = new Set([
	'Directo',
	'Directo (sin partner)',
	'PLATFORM',
	'',
	null,
	undefined
]);

/**
 * transfer.origin del funnel del partner Indexa. Por el acuerdo con Indexa, quien
 * llega por aquí (hoy solo autónomos) únicamente puede ver/elegir planes de Indexa,
 * nunca de otras gestoras (Horos, Caser). Se compara normalizado (trim + minúsculas).
 */
const INDEXA_PARTNER_ORIGIN = 'indexacapital';
const INDEXA_MANAGER = 'Indexa Capital';

function isIndexaPartnerOrigin(origin?: string | null): boolean {
	return !!origin && origin.trim().toLowerCase() === INDEXA_PARTNER_ORIGIN;
}

/**
 * Returns available plans filtered by user type (freelance vs individual).
 * Freelancers see only isFreelanceOnly plans, individuals see only non-freelance plans.
 */
export function getAvailablePlansByUserType(
	isFreelance: boolean,
	allPlans: ArcaPlan[] = ARCA_PLANS
): ArcaPlan[] {
	return allPlans.filter((plan) => {
		if (isFreelance) {
			return plan.isFreelanceOnly;
		}
		return !plan.isFreelanceOnly;
	});
}

/**
 * Returns available plans filtered by partner origin.
 * - Indexa partner funnel (`indexacapital`): solo planes de Indexa, por acuerdo
 *   contractual — nunca Horos ni Caser. Aplica tanto a "ver" como a "cambiar".
 * - Resto de partners: se excluyen los planes reservados a tráfico directo
 *   (`DIRECT_ONLY_PLANS`, hoy vacío).
 */
export function getAvailablePlans(
	origin?: string | null,
	allPlans: ArcaPlan[] = ARCA_PLANS
): ArcaPlan[] {
	if (isIndexaPartnerOrigin(origin)) {
		return allPlans.filter((plan) => plan.manager === INDEXA_MANAGER);
	}
	if (origin && !DIRECT_ORIGINS.has(origin)) {
		return allPlans.filter((plan) => !DIRECT_ONLY_PLANS.has(plan.id));
	}
	return [...allPlans];
}

/** Deprecated plan IDs that fallback to HOROS */
const DEPRECATED_PLAN_FALLBACK: Record<string, ArcaPlanId> = {
	cobas: ArcaPlanId.HOROS
};

export function findArcaPlanById(id: string, plans: ArcaPlan[] = ARCA_PLANS): ArcaPlan | undefined {
	const resolvedId = DEPRECATED_PLAN_FALLBACK[id] || id;
	return plans.find((plan) => plan.id === resolvedId);
}

/** Legacy plan names stored in transfer metadata before the catalog name correction. */
const PLAN_NAME_ALIASES: Record<string, ArcaPlanId> = {
	'magallanes acciones europeas pp': ArcaPlanId.MAGALLANES,
	'horos internacional, pp': ArcaPlanId.HOROS,
	'horos internacional pp': ArcaPlanId.HOROS,
	'horos value internacional pp': ArcaPlanId.HOROS,
	'indexa más rentabilidad pp': ArcaPlanId.INDEXA,
	'indexa mas rentabilidad pp': ArcaPlanId.INDEXA,
	'indexa más rentabilidad acciones, ppi': ArcaPlanId.INDEXA,
	'indexa mas rentabilidad acciones, ppi': ArcaPlanId.INDEXA
};

export function findArcaPlanByName(
	name: string,
	plans: ArcaPlan[] = ARCA_PLANS
): ArcaPlan | undefined {
	const normalized = name.toLowerCase().trim();
	const direct = plans.find((plan) => plan.name.toLowerCase() === normalized);
	if (direct) return direct;
	const aliasId = PLAN_NAME_ALIASES[normalized];
	return aliasId ? plans.find((p) => p.id === aliasId) : undefined;
}
